/**
 * Enhanced Modal Component
 * 
 * Features:
 * - Full accessibility support (ARIA attributes, focus trapping)
 * - Multiple close options (close button, overlay click, ESC key)
 * - Animation options
 * - Z-index management for multiple modals
 * - Callbacks for open/close events
 * - Proper error handling
 */

class Modal {
    constructor(options = {}) {
        this.options = {
            debug: options.debug || false,
            ...options
        };
        
        // Modal state management using WeakMap for better garbage collection
        this.modalState = new WeakMap();
        this.zIndexCounter = 1000;
        this.activeModals = [];
        
        this.initialize();
    }

    /**
     * Debug logging function
     * @private
     */
    log(...args) {
        if (this.options.debug) {
            console.log('[Modal]', ...args);
        }
    }

    initialize() {
        // Find and initialize all modal containers
        const allModalContainers = document.querySelectorAll('pageblock-modal[data-modal-id], [data-pb-modal="container"][data-modal-id]');

        allModalContainers.forEach(container => {
            this.initializeModalContainer(container);
        });

        // For backward compatibility, also initialize the old style modals
        const legacySheets = document.querySelectorAll('[data-pb-modal="sheet"][data-modal-id]');
        legacySheets.forEach(sheet => {
            if (!sheet.closest('pageblock-modal')) {
                this.initializeLegacyModal(sheet);
            }
        });

        // Listen for click events with delegation
        document.addEventListener('click', this.handleDocumentClick.bind(this));

        // ESC key and Tab key support
        document.addEventListener('keydown', this.handleKeyDown.bind(this));

        // Custom event listeners
        document.addEventListener('pb:modal:open', this.handleCustomOpenEvent.bind(this));
        document.addEventListener('pb:modal:close', this.handleCustomCloseEvent.bind(this));
        
        this.log('Modal system initialized');
    }

    initializeModalContainer(container) {
        const modalId = container.getAttribute('data-modal-id');
        if (!modalId) {
            console.warn('Modal container found without data-modal-id attribute', container);
            return;
        }

        // Find sheet and overlay within this container
        const modal = container.querySelector('[data-pb-modal="sheet"]');
        const overlay = container.querySelector('[data-pb-modal="overlay"]');

        if (!modal || !overlay) {
            console.warn(`Modal components missing in container with ID: ${modalId}`, container);
            return;
        }

        // Set initial ARIA attributes
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-hidden', 'true');

        // Apply animation and variant from container to modal if available
        const animation = container.getAttribute('data-animation');
        const variant = container.getAttribute('data-variant');

        // Store initial state
        this.modalState.set(container, {
            id: modalId,
            isOpen: false,
            previousFocus: null,
            animation: animation || 'fade',
            variant: variant || 'default',
            zIndex: parseInt(modal.style.zIndex) || this.zIndexCounter,
            modal: modal,
            overlay: overlay,
            focusableElements: this.getFocusableElements(modal)
        });
        
        this.log(`Initialized modal: ${modalId}`);
    }

    initializeLegacyModal(sheet) {
        const modalId = sheet.getAttribute('data-modal-id');
        if (!modalId) return;
        
        // Find or create overlay
        let overlay = document.querySelector('[data-pb-modal="overlay"]');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.setAttribute('data-pb-modal', 'overlay');
            document.body.appendChild(overlay);
        }
        
        // Set initial ARIA attributes
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'true');
        sheet.setAttribute('aria-hidden', 'true');
        
        // Create a virtual container for state management
        const virtualContainer = {
            getAttribute: (attr) => attr === 'data-modal-id' ? modalId : null,
            dispatchEvent: (event) => sheet.dispatchEvent(event)
        };
        
        // Store initial state
        this.modalState.set(virtualContainer, {
            id: modalId,
            isOpen: false,
            previousFocus: null,
            animation: 'fade',
            variant: 'default',
            zIndex: parseInt(sheet.style.zIndex) || this.zIndexCounter,
            modal: sheet,
            overlay: overlay,
            focusableElements: this.getFocusableElements(sheet),
            isLegacy: true
        });
        
        this.log(`Initialized legacy modal: ${modalId}`);
    }

    handleDocumentClick(event) {
        // Trigger check
        const trigger = event.target.closest('[data-pb-modal-trigger]');
        if (trigger) {
            event.preventDefault();
            const modalId = trigger.getAttribute('data-pb-modal-trigger');
            this.openModal(modalId, {
                trigger: trigger
            });
        }

        // Close button check
        const closeButton = event.target.closest('[data-pb-modal="close"]');
        if (closeButton) {
            event.preventDefault();
            const container = closeButton.closest('pageblock-modal, [data-pb-modal="container"]');
            const modalId = container?.getAttribute('data-modal-id');
            
            // For legacy modals
            const sheet = closeButton.closest('[data-pb-modal="sheet"]');
            const legacyModalId = sheet?.getAttribute('data-modal-id');
            
            this.closeModal(modalId || legacyModalId);
        }

        // Overlay check
        const overlay = event.target.closest('[data-pb-modal="overlay"]');
        if (overlay && overlay === event.target) {
            event.preventDefault();
            
            // Find which modal this overlay belongs to
            if (this.activeModals.length > 0) {
                // Close the topmost modal
                const topModalId = this.activeModals[this.activeModals.length - 1];
                this.closeModal(topModalId);
            }
        }
    }

    handleKeyDown(event) {
        if (event.key === 'Escape' && this.activeModals.length > 0) {
            // Close the topmost modal
            const topModalId = this.activeModals[this.activeModals.length - 1];
            this.closeModal(topModalId);
        } else if (event.key === 'Tab' && this.activeModals.length > 0) {
            // Handle focus trapping for the active modal
            const topModalId = this.activeModals[this.activeModals.length - 1];
            const container = this.findModalContainer(topModalId);

            if (container) {
                const state = this.modalState.get(container);
                if (state && state.modal) {
                    this.trapFocus(event, state.modal);
                }
            }
        }
    }

    handleCustomOpenEvent(event) {
        const modalId = event.detail.modalId;
        const options = event.detail.options || {};
        this.openModal(modalId, options);
    }

    handleCustomCloseEvent(event) {
        const modalId = event.detail.modalId;
        const options = event.detail.options || {};
        this.closeModal(modalId, options);
    }

    findModalContainer(modalId) {
        // First try to find a modern container
        let container = document.querySelector(`pageblock-modal[data-modal-id="${modalId}"], [data-pb-modal="container"][data-modal-id="${modalId}"]`);
        
        // If not found, check for legacy modals
        if (!container) {
            // For legacy modals, we need to find the virtual container in our state
            // We can't use entries() on WeakMap, so we need a different approach
            const legacySheet = document.querySelector(`[data-pb-modal="sheet"][data-modal-id="${modalId}"]`);
            if (legacySheet) {
                // Create a virtual container like we did in initializeLegacyModal
                container = {
                    getAttribute: (attr) => attr === 'data-modal-id' ? modalId : null,
                    dispatchEvent: (event) => legacySheet.dispatchEvent(event)
                };
            }
        }
        
        return container;
    }

    openModal(modalId, options = {}) {
        if (!modalId) {
            console.error("Modal ID is required to open a modal");
            return;
        }

        const container = this.findModalContainer(modalId);
        if (!container) {
            console.error(`Modal container not found for ID: ${modalId}`);
            return;
        }

        // Get modal state
        const state = this.modalState.get(container);
        if (!state) {
            console.error(`Modal state not found for ID: ${modalId}`);
            return;
        }

        const modal = state.modal;
        const overlay = state.overlay;

        // If already open, do nothing
        if (state.isOpen) return;

        // Store the element that had focus before opening the modal
        state.previousFocus = document.activeElement;

        // Update z-index to bring this modal to the front
        this.zIndexCounter += 2;
        state.zIndex = this.zIndexCounter;

        modal.style.zIndex = state.zIndex;
        overlay.style.zIndex = state.zIndex - 1;

        // Update ARIA attributes
        modal.setAttribute('aria-hidden', 'false');

        // Add active class for CSS transitions
        modal.classList.add('cc-active');
        overlay.classList.add('cc-active');

        // Apply animation type if specified in options (overrides container attribute)
        if (options.animation && !state.isLegacy) {
            container.dataset.animation = options.animation;
        }

        // Apply variant if specified in options (overrides container attribute)
        if (options.variant && !state.isLegacy) {
            container.dataset.variant = options.variant;
        }

        // Prevent body scrolling
        document.body.style.overflow = 'hidden';

        // Add to active modals stack
        this.activeModals.push(modalId);

        // Update state
        state.isOpen = true;
        this.modalState.set(container, state);

        // Set focus to the first focusable element inside the modal
        setTimeout(() => {
            const focusTarget = modal.querySelector('[data-pb-modal-autofocus]') ||
                state.focusableElements[0] ||
                modal;

            if (focusTarget) {
                focusTarget.focus();
            }
        }, 50);

        // Dispatch custom event
        container.dispatchEvent(new CustomEvent('modal:opened', {
            detail: {
                modalId,
                options
            }
        }));

        // Call the onOpen callback if provided
        if (typeof options.onOpen === 'function') {
            options.onOpen(modal, overlay, container);
        }
        
        this.log(`Opened modal: ${modalId}`);
    }

    closeModal(modalId, options = {}) {
        if (!modalId) {
            console.error("Modal ID is required to close a modal");
            return;
        }

        const container = this.findModalContainer(modalId);
        if (!container) {
            console.error(`Modal container not found for ID: ${modalId}`);
            return;
        }

        // Get modal state
        const state = this.modalState.get(container);
        if (!state || !state.isOpen) return;

        const modal = state.modal;
        const overlay = state.overlay;

        // Update ARIA attributes
        modal.setAttribute('aria-hidden', 'true');

        // Remove active class for CSS transitions
        modal.classList.remove('cc-active');
        
        // Only remove overlay active class if this is the last modal using this overlay
        let otherModalsUsingOverlay = false;
        
        // We can't use entries() on WeakMap, so we need a different approach
        // Check if any other active modals are using the same overlay
        for (let i = 0; i < this.activeModals.length; i++) {
            const activeId = this.activeModals[i];
            if (activeId !== modalId) {
                const activeContainer = this.findModalContainer(activeId);
                if (activeContainer) {
                    const activeState = this.modalState.get(activeContainer);
                    if (activeState && activeState.overlay === overlay) {
                        otherModalsUsingOverlay = true;
                        break;
                    }
                }
            }
        }
        
        if (!otherModalsUsingOverlay) {
            overlay.classList.remove('cc-active');
        }

        // Remove from active modals stack
        const index = this.activeModals.indexOf(modalId);
        if (index > -1) {
            this.activeModals.splice(index, 1);
        }

        // If no more active modals, restore body scrolling
        if (this.activeModals.length === 0) {
            document.body.style.overflow = '';
        }

        // Update state
        state.isOpen = false;
        this.modalState.set(container, state);

        // Listen for the transition to end before restoring focus
        const transitionEndHandler = () => {
            // Restore focus to the element that had focus before opening
            if (state.previousFocus && state.previousFocus.focus) {
                state.previousFocus.focus();
            }

            // Dispatch custom event
            container.dispatchEvent(new CustomEvent('modal:closed', {
                detail: {
                    modalId,
                    options
                }
            }));

            // Call the onClose callback if provided
            if (typeof options.onClose === 'function') {
                options.onClose(modal, overlay, container);
            }

            modal.removeEventListener('transitionend', transitionEndHandler);
        };

        modal.addEventListener('transitionend', transitionEndHandler);
        
        this.log(`Closed modal: ${modalId}`);
    }

    closeAllModals() {
        // Make a copy of the array since we'll be modifying it while iterating
        const modalsToClose = [...this.activeModals];

        modalsToClose.forEach(modalId => {
            this.closeModal(modalId);
        });
        
        this.log('Closed all modals');
    }

    // Utility function to get all focusable elements within a container
    getFocusableElements(container) {
        const focusableSelectors = [
            'a[href]:not([disabled])',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ];

        return Array.from(container.querySelectorAll(focusableSelectors.join(',')));
    }

    // Focus trap implementation
    trapFocus(event, modal) {
        const container = modal.closest('pageblock-modal, [data-pb-modal="container"]') || 
                         this.findModalContainer(modal.getAttribute('data-modal-id'));
        
        if (!container) return;
        
        const state = this.modalState.get(container);
        if (!state) return;

        const focusableElements = state.focusableElements;
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Handle tab and shift+tab to trap focus within the modal
        if (event.shiftKey) {
            if (document.activeElement === firstElement) {
                lastElement.focus();
                event.preventDefault();
            }
        } else {
            if (document.activeElement === lastElement) {
                firstElement.focus();
                event.preventDefault();
            }
        }
    }

    // Public API methods
    open(modalId, options = {}) {
        this.openModal(modalId, options);
    }

    close(modalId, options = {}) {
        this.closeModal(modalId, options);
    }

    closeAll() {
        this.closeAllModals();
    }

    destroy() {
        // Remove event listeners
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('pb:modal:open', this.handleCustomOpenEvent);
        document.removeEventListener('pb:modal:close', this.handleCustomCloseEvent);
        
        // Close all modals
        this.closeAllModals();
        
        // Clear state
        this.modalState = new WeakMap();
        this.activeModals = [];
        
        this.log('Modal system destroyed');
    }
}

// For backward compatibility
export default Modal;
export { Modal }; 